import { FieldValues, Control, Path } from 'react-hook-form';
import * as React from 'react';
import { JSX } from 'react';

import {
  SelectPlaceholder,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
  Select,
} from '../select/select';
import { FormControl, FormMessage, FormField, FormItem } from '../form';
import { cn } from '../../utils';

interface IFormSelectProps<T extends FieldValues = FieldValues> {
  componentProps?: Partial<React.ComponentProps<typeof Select>> & { hasShadow?: boolean };
  options: { label: JSX.Element | string; value: string | null; disabled?: boolean }[];
  size?: 'medium' | 'small' | 'large';
  formControl: Control<T>;
  placeholder?: string;
  hideErrorMessage?: boolean;
  disabled?: boolean;
  floating?: boolean;
  className?: string;
  required?: boolean;
  name: Path<T>;
}

export function FormSelect<T extends FieldValues>({
  hideErrorMessage,
  componentProps,
  placeholder,
  formControl,
  className,
  floating,
  disabled,
  required,
  options,
  size,
  name,
}: IFormSelectProps<T>) {
  const { hasShadow, ...componentPropsRest } = componentProps || {};

  return (
    <FormField
      render={({ field, formState }) => {
        const hasError = !!formState.errors[field.name];

        return (
          <FormItem className={cn('relative z-0', className)}>
            <FormControl>
              <>
                <Select
                  {...componentPropsRest}
                  onValueChange={(value) => {
                    field.onChange(value);
                    field.onBlur();
                  }}
                  value={field.value}
                  disabled={disabled}
                >
                  <FormControl>
                    <SelectTrigger
                      hasShadow={hasShadow}
                      required={required}
                      hasError={hasError}
                      size={size}
                    >
                      <SelectPlaceholder
                        placeholder={placeholder}
                        hasValue={!!field.value}
                        required={required}
                        floating={floating}
                      >
                        <SelectValue />
                      </SelectPlaceholder>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {options.map((option) => (
                      <SelectItem
                        disabled={option.disabled}
                        value={option.value!}
                        key={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            </FormControl>
            {!hideErrorMessage && <FormMessage />}
          </FormItem>
        );
      }}
      control={formControl}
      name={name}
    />
  );
}
